import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ChartLine, GraphIcon, Sparkle, Bug, Wrench, Stack, ChatCircleText,
  SignOut, CircleNotch, UploadSimple
} from "@phosphor-icons/react";
import ClientSwitcher from "./ClientSwitcher";

const NAV = [
  { to: "/", label: "Executive", icon: ChartLine, end: true },
  { to: "/intake", label: "Intake & Intelligence", icon: UploadSimple },
  { to: "/observatory", label: "QA Observatory", icon: Stack },
  { to: "/workflows", label: "Workflow Graph", icon: GraphIcon },
  { to: "/scenarios", label: "AI Scenarios", icon: Sparkle },
  { to: "/regression", label: "Regression Optimizer", icon: Wrench },
  { to: "/defects", label: "Defect Intelligence", icon: Bug },
  { to: "/orchestration", label: "Orchestration", icon: CircleNotch },
  { to: "/sme", label: "SME Assistant", icon: ChatCircleText },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  function handleLogout() {
    logout();
    nav("/login");
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-zinc-50">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className="w-[280px] flex-shrink-0 border-r border-zinc-200 bg-white flex flex-col"
      >
        {/* Brand */}
        <div className="px-6 pt-6 pb-5 border-b border-zinc-200">
          <div className="flex items-center gap-3 mb-3" data-testid="brand-mark">
            <div className="w-11 h-11 bg-zinc-950 flex items-center justify-center flex-shrink-0 rounded-sm">
              <img src="/vlink-logo.png" alt="VLink" className="w-9 h-9 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-base tracking-tight leading-tight text-zinc-900">
                Workflow Assurance<span className="text-vlink-500">.</span>
              </div>
              <div className="text-[10px] text-zinc-400 font-mono uppercase tracking-widest">
                AI-Driven · Client-Agnostic
              </div>
            </div>
          </div>
        </div>

        {/* Client switcher */}
        <div className="px-4 py-4 border-b border-zinc-200">
          <ClientSwitcher />
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-vlink-500 bg-vlink-50 text-zinc-900 font-medium"
                    : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <Icon size={16} weight="regular" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-200 px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Signed in as</div>
          <div className="text-sm text-zinc-900 font-medium" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="mt-1 flex items-center gap-1.5">
            <RoleBadge role={user?.role} />
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-600 hover:text-vlink-600 transition"
          >
            <SignOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto" data-testid="app-main">
        {children}
      </main>
    </div>
  );
}

function RoleBadge({ role }) {
  const map = {
    client_executive: { label: "Client Executive", tone: "bg-vlink-50 text-vlink-800 border-vlink-200" },
    client_manager:   { label: "Client Manager",   tone: "bg-amber-50 text-amber-800 border-amber-200" },
    vlink_executive:  { label: "VLink Executive",  tone: "bg-zinc-900 text-white border-zinc-900" },
    vlink_admin:      { label: "VLink Admin",      tone: "bg-rose-50 text-rose-800 border-rose-200" },
  };
  const r = map[role] || { label: role || "user", tone: "bg-zinc-100 text-zinc-800 border-zinc-200" };
  return (
    <span data-testid="sidebar-user-role" className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium border rounded-sm ${r.tone}`}>
      {r.label}
    </span>
  );
}
